import { Breadcrumb } from 'components/knowledge-base/breadcrumbs/Breadcrumbs'
import { ArticlesArrowComponent, KBWrapper } from 'components/knowledge-base/Categories'
import GarrySearchBar from 'components/knowledge-base/GarrySearchBar'
import SocialMediaLinks from 'components/knowledge-base/SocialMediaLinks'
import Tags from 'components/knowledge-base/Tags'
import StaticPageLayout from 'components/StaticPageLayout'
import InternalLink from 'components/UI/InternalLink'
import { ReactElement, useState } from 'react'
import { Flex, Text, get } from 'theme-ui'
import getArticleSlug from 'utils/getArticleSlug'
import getEnglishStrings from 'utils/getEnglishStrings'
import { allPlatformsId, getPlatformCategoryTags } from 'utils/getPlatformCategoryTags'
import slugify from 'utils/slugify'
import usePlatformTagId from 'utils/usePlatformTagId'
import TitleAndSubtitle from '../../UI/TitleAndSubtitle'

export interface CategoryArticlesContext {
  title: string
  articlesInCategoryOrTag: Queries.DirectusData_knowledge_base_articles[]
  pageData: Queries.DirectusData_knowledge_base_page
}

export default function CategoryArticles({
  pageContext: { title, articlesInCategoryOrTag, pageData },
}: {
  pageContext: CategoryArticlesContext
}): ReactElement {
  const [hasInputValue, setHasInputValue] = useState(false)
  const { selectedPlatformTagId, setSelectedPlatformTagId } = usePlatformTagId()
  const [selectedNonPlatformTagId, setSelectedNonPlatformTagId] =
    useState<string | undefined>(undefined)
  const pageDataStrings = getEnglishStrings(pageData?.strings)
  const allCategoryTags = articlesInCategoryOrTag.map(article => article.tags).flat()

  const nonPlatformCategoryTags = allCategoryTags
    .filter(tag => !tag?.knowledge_base_tags_id?.is_platform)
    .map(tag => {
      return {
        id: tag?.knowledge_base_tags_id?.slug ?? '',
        label: getEnglishStrings(tag?.knowledge_base_tags_id?.strings)?.name ?? '',
      }
    })

  const platformCategoryTags = getPlatformCategoryTags(articlesInCategoryOrTag)
  const uniqueTags = nonPlatformCategoryTags.filter(
    (tag, index) => nonPlatformCategoryTags.findIndex(t => tag.id === t.id) === index,
  )

  const articlesToBeDisplayed = articlesInCategoryOrTag.filter(articleInCategoryOrTag => {
    const matchesPlatformFilter =
      selectedPlatformTagId === allPlatformsId ||
      articleInCategoryOrTag.tags?.find(
        tag => tag?.knowledge_base_tags_id?.slug === selectedPlatformTagId,
      )

    const matchesNonPlatformFilter =
      !selectedNonPlatformTagId ||
      articleInCategoryOrTag.tags?.find(
        tag => tag?.knowledge_base_tags_id?.slug === selectedNonPlatformTagId,
      )

    return matchesPlatformFilter && matchesNonPlatformFilter
  })
  const breadcrumbs: Breadcrumb[] = [
    {
      title: 'HELP',
      href: selectedPlatformTagId
        ? `/knowledge-base?platform=${selectedPlatformTagId}`
        : '/knowledge-base',
    },
    {
      title: title ?? '',
      href: `/knowledge-base/${title === 'Popular' ? 'tags' : 'categories'}/${slugify(title)}`,
    },
  ]

  return (
    <StaticPageLayout
      title={`${title} | Windscribe`}
      metaDescription={pageDataStrings?.meta_description}
      metaKeywords={pageDataStrings?.meta_keywords}
    >
      <TitleAndSubtitle title={pageDataStrings?.title} subtitle={pageDataStrings?.subtitle} />
      <GarrySearchBar hasInputValue={hasInputValue} setHasInputValue={setHasInputValue} />
      {!hasInputValue && (
        <>
          <Flex sx={{ mb: '4rem' }}>
            <Tags
              tags={platformCategoryTags}
              selectedTagId={selectedPlatformTagId}
              setSelectedTagId={setSelectedPlatformTagId}
              shouldAllowNoSelection={false}
            />
          </Flex>
          <KBWrapper>
            <Flex
              data-testid="category-article"
              sx={{
                maxWidth: '133rem',
                flexDirection: 'column',
                pl: '2.4rem',
                pt: '2.4rem',
                pb: '1.6rem',
                width: '100%',
              }}
            >
              <InternalLink
                sx={{
                  gap: '1.6rem',
                  '&:hover': { path: { strokeOpacity: 1 }, span: { color: 'white' } },
                  width: 'max-content',
                  mb: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
                href="/knowledge-base"
              >
                <ArticlesArrowComponent
                  sxx={{
                    transform: 'rotate(180deg)',
                    width: '2.4rem',
                    height: '2.4rem',
                    path: {
                      strokeOpacity: 1,
                    },
                  }}
                />
                <Text variant="h3Big" data-testid="category-article-count">{`${title}${
                  breadcrumbs[1].href?.includes('tags') ? ' Articles' : ''
                } (${articlesToBeDisplayed.length})`}</Text>
              </InternalLink>
              {articlesToBeDisplayed.map((articleInCategory, index) => {
                const articleString = getEnglishStrings(articleInCategory.strings)
                const articleSlug = getArticleSlug(articleInCategory)
                return (
                  <Flex
                    key={`${articleInCategory.slug} ${index}`}
                    data-testid={`category-article-${articleSlug}`}
                    sx={{
                      py: '0.8rem',
                      borderBottom:
                        index === articlesToBeDisplayed.length - 1 ? 'unset' : '0.2rem solid',
                      borderBottomColor:
                        index === articlesToBeDisplayed.length - 1 ? 'unset' : 'white15',
                      transition: theme => `${get(theme, 'transitions.default')}`,
                      ':hover': {
                        '.articles-arrow path': {
                          strokeOpacity: 1,
                        },
                        span: {
                          color: 'white',
                        },
                        cursor: 'pointer',
                      },
                    }}
                  >
                    {/*@ts-ignore*/}
                    <InternalLink
                      href={`/knowledge-base/articles/${articleSlug}`}
                      sxx={{ width: '100%', pr: '1.6rem' }}
                    >
                      <Flex
                        sx={{
                          display: 'flex',
                          textDecoration: 'none',
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Text data-testid="article-title" variant="paragraphSmallRegular">
                          {articleString?.title}
                        </Text>
                        <ArticlesArrowComponent className="articles-arrow" />
                      </Flex>
                    </InternalLink>
                  </Flex>
                )
              })}
            </Flex>
          </KBWrapper>
        </>
      )}
      <Flex sx={{ marginTop: ['4.8rem', '8rem'] }}>
        <SocialMediaLinks />
      </Flex>
    </StaticPageLayout>
  )
}
